import React, { useEffect, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
import { featureKeys } from 'services/FeatureFlagService';
import { withTheme } from 'styled-components';
import { compose } from 'redux';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

import {
  Button,
  Select,
  Input,
  Checkbox,
  SVGIcon,
} from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import Modal, { ModalContent, ModalActions } from 'components/Modal';
import Circular from 'components/ProgressBar/Circular';
import StatusInfo from './ExportStatusInfo';
import { publishFormat, publishType, status, OCULUS_PUBLISHED_LIVE, tableOfContents } from '../../utils/constants';
import { gtmTrackAuthoringPublicationUserInteraction } from 'utils/gtm/handler';
import messages from './messages';
import {
  FormField,
  InfoCard,
  Note,
  InfoCardList,
  InfoCardLabel,
  CircularProgress,
  AugmentedModalContent,
  TocLabel,
  TocItem,
  TocFeedback,
} from './styles';
import { GTM_EVENT_ACTION, GTM_EVENT_CATEGORY } from 'utils/gtm/constants';
import { setCoursewareContext, clearCoursewareContext } from 'utils/coursewareContext';

const initialState = {
  productId: '',
  type: '',
  note: false,
  typeFeedback: false,
  noteFeedback: false,
  productIdFeedback: '',
  showProgress: false,
  tocLevel: '',
  tocLevelFeedback: false,
  disablePublishButton: false,
};

const actionType = Object.freeze({
  TYPE: 'type',
  NOTE: 'note',
  PRODUCT_ID: 'productId',
  DISCIPLINE: 'discipline',
  PREVIEW_URL: 'previewUrl',
  TYPE_FEEDBACK: 'typeFeedback',
  NOTE_FEEDBACK: 'noteFeedback',
  PRODUCT_ID_FEEDBACK: 'productIdFeedback',
  INIT: 'init',
  SHOW_PROGRESS: 'showProgress',
  PERCENTAGE: 'percentage',
  TOC_LEVEL: 'tocLevel',
  TOC_LEVEL_FEEDBACK: 'tocLevelFeedback',
  DISABLE_PUBLISH_BUTTON: 'disablePublishButton',
});

const reducer = (state, action) => {
  if (action.type === actionType.INIT) {
    return initialState;
  }
  const result = { ...state };
  result[action.type] = action.value;
  return result;
};

const ExportPearsonPlus = ({
  visible,
  onPublish,
  onDismiss,
  configSettings,
  theme,
  oculusStatus,
  exportStatus,
  exportSnippetsCompleted = 0,
  totalExportSnippet = 0,
  isPearsonPlusTest,
  productIdPDZ,
  getFeatureFlagValue,
  disabledTocLevel,
  market,
  publicationToPublish,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const isTocOptionsFFEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATION_THREE_LEVEL_TOC_OPTION_BV);
  const { type, note, noteFeedback, typeFeedback, productId, productIdFeedback, showProgress, percentage, tocLevel, tocLevelFeedback, disablePublishButton } = state;
  const { originalProductId, productId: configProductId } = configSettings || {};
  const { activityId } = publicationToPublish;

  /**
 * @description handles type change action
 */
  const typeChange = (event) => {
    
    gtmTrackAuthoringPublicationUserInteraction({
      productId,
      eventCategory: GTM_EVENT_CATEGORY.COURSE_PUBLICATION_CREATION,
      eventAction: GTM_EVENT_ACTION.SELECT_COURSE_PUBLICATION_OUTPUT,
      eventLabel:intl.formatMessage(messages.publishType),
      eventValue: event.currentTarget.value,
    });
    dispatch({ type: actionType.TYPE, value: event.currentTarget.value });
    dispatch({ type: actionType.TYPE_FEEDBACK, value: false });
  };

  /**
  * @description handles publication note change action
  */
  const noteChange = (event) => {
    if (event.currentTarget.value.trim()) {
      dispatch({ type: actionType.NOTE, value: event.currentTarget.value });
      dispatch({ type: actionType.NOTE_FEEDBACK, value: false });
    } else {
      dispatch({ type: actionType.NOTE, value: null });
    }
  };

  /**
  * @description handles publication product ID change action
  */
  const productIDChange = (event) => {
    if (event.currentTarget.value.trim()) {
      dispatch({ type: actionType.PRODUCT_ID, value: event.currentTarget.value });
      dispatch({ type: actionType.PRODUCT_ID_FEEDBACK, value: '' });
    } else {
      dispatch({ type: actionType.PRODUCT_ID, value: null });
    }
  };

  /**
  * @description handles publication product ID change on blur
  */
  const handleProductIdBlur = async () => {
    const productIdInfo = await productIdPDZ({ productId, activityId, market });
    if (productIdInfo?.error?.code === 400) {
      dispatch({ type: actionType.PRODUCT_ID_FEEDBACK, value: productIdInfo.error.message });
    }
  };

  /**
   * @description handles modal dismiss and initialization
   */
  const handleDismiss = useCallback(() => {
    initialize();
    onDismiss();
  }, [initialize, onDismiss]);


  const initialize = useCallback(() => {
    dispatch({ type: actionType.INIT });
  }, []);

  const settings = {
    "productId": productId ? productId : "",
  };

  const handlePublish = async () => {
    gtmTrackAuthoringPublicationUserInteraction({
      productId,
      eventCategory: GTM_EVENT_CATEGORY.COURSE_PUBLICATION_CREATION,
      eventAction: GTM_EVENT_ACTION.CLICK_TO_COMPLETE_PUBLICATION_CREATION,
      eventLabel: intl.formatMessage(messages.publishAllCaps),
      eventValue: isPearsonPlusTest
        ? publishFormat.PEARSON_PLUS_TEST
        : publishFormat.PEARSON_PLUS,
    });
    /* Added to disable the publish button once clicked to avoid multiple publish call. */
    dispatch({ type: actionType.DISABLE_PUBLISH_BUTTON, value: true });

    if (await validateFields()) {
      gtmTrackAuthoringPublicationUserInteraction({
        productId,
        eventCategory: GTM_EVENT_CATEGORY.COURSE_PUBLICATION,
        eventAction: GTM_EVENT_ACTION.CLICK_TO_START_FORMAL_PUBLICATION,
        eventLabel: intl.formatMessage(messages.publishAllCaps),
        eventValue: isPearsonPlusTest
          ? publishFormat.PEARSON_PLUS_TEST
          : publishFormat.PEARSON_PLUS,
      });
      onPublish({
        format: isPearsonPlusTest
          ? publishFormat.PEARSON_PLUS_TEST
          : publishFormat.PEARSON_PLUS,
        type,
        note,
        productId,
        ...(market && { market }),
        settings,
        ...(isTocOptionsFFEnabled && { tocLevel }),
        ...(isTocOptionsFFEnabled && { metadata: { tocLevel } }),
      });
    } else {
      /* Enable publish button when validation fails*/
      dispatch({ type: actionType.DISABLE_PUBLISH_BUTTON, value: false });
    }
    return;
  };

  const validateFields = async () => {
    let missingField = false;
    if (!type) {
      missingField = true;
      dispatch({ type: actionType.TYPE_FEEDBACK, value: true });
    }
    if (!note) {
      missingField = true;
      dispatch({ type: actionType.NOTE_FEEDBACK, value: true });
    }
    if (isTocOptionsFFEnabled && !tocLevel?.length) {
      missingField = true;
      dispatch({ type: actionType.TOC_LEVEL_FEEDBACK, value: true });
    }
    if (!productId) {
      missingField = true;
      dispatch({ type: actionType.PRODUCT_ID_FEEDBACK, value: intl.formatMessage(messages.productIdFeedback) });
    } else {
      const productIdInfo = await productIdPDZ({ productId, activityId, market });
      if (productIdInfo?.error?.code === 400) {
        dispatch({ type: actionType.PRODUCT_ID_FEEDBACK, value: productIdInfo.error.message });
        missingField = true;
      }
    }

    return !missingField;
  };

  const isProductIdDisabled = !isPearsonPlusTest && oculusStatus?.toUpperCase() === OCULUS_PUBLISHED_LIVE;

  const handleModalDismiss = useCallback((percent) => {
    if (percent) {
      dispatch({ type: actionType.PERCENTAGE, value: percent });
    }
    setTimeout(() => {
      handleDismiss();
    }, 1000);
  }, [handleDismiss]);

  useEffect(() => {
    if (!visible || !activityId) {
      return undefined;
    }
    setCoursewareContext({ courseId: activityId });
    return () => clearCoursewareContext();
  }, [visible, activityId]);

  useEffect(() => {
    initialize();
    if (originalProductId || configProductId) {
      dispatch({ type: actionType.PRODUCT_ID, value: originalProductId || configProductId });
      dispatch({ type: actionType.PRODUCT_ID_FEEDBACK, value: '' });
    }
    if (isTocOptionsFFEnabled && disabledTocLevel !== '') {
      dispatch({ type: actionType.TOC_LEVEL, value: disabledTocLevel });
      dispatch({ type: actionType.METADATA, value: { ...state.metadata, tocLevel: disabledTocLevel } });
      dispatch({ type: actionType.TOC_LEVEL_FEEDBACK, value: false });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialize, originalProductId, configProductId, disabledTocLevel, isTocOptionsFFEnabled]);

  useEffect(() => {
    let timer;
    switch (exportStatus) {
      case status.INPROGRESS: {
        const completedPercentage = Math.floor((exportSnippetsCompleted / totalExportSnippet || 0) * 100);
        dispatch({ type: actionType.SHOW_PROGRESS, value: true });
        dispatch({ type: actionType.MODAL_TITLE, value: intl.formatMessage(messages.exportTitle) });
        dispatch({ type: actionType.PERCENTAGE, value: completedPercentage });
        break;
      }
      case status.COMPLETED:
        handleModalDismiss(100);
        break;
      case status.FAILED:
        handleModalDismiss();
        break;
      default:
        break;
    }
    return () => { clearInterval(timer); };
  }, [exportStatus, handleModalDismiss, exportSnippetsCompleted, totalExportSnippet]);
  const tocOptions = Object.values(tableOfContents);

  const tocLevelChange = (event) => {
    if (!disabledTocLevel) {
      gtmTrackAuthoringPublicationUserInteraction({
        productId,
        eventCategory: GTM_EVENT_CATEGORY.COURSE_PUBLICATION,
        eventAction: GTM_EVENT_ACTION.SELECT_ORGNISATION_LEVEL,
        eventLabel: intl.formatMessage(messages.organizationalLevel),
        eventValue: event.currentTarget.value,
      });
    }
    dispatch({ type: actionType.TOC_LEVEL, value: event.currentTarget.value });
    dispatch({ type: actionType.METADATA, value: { ...state.metadata, tocLevel: event.currentTarget.value } });
    dispatch({ type: actionType.TOC_LEVEL_FEEDBACK, value: false });
  };

  return (
    <Modal icon="duotonePublications" visible={visible} onDismiss={handleDismiss} title={isPearsonPlusTest ? intl.formatMessage(messages.publishPearsonPlusTest) : intl.formatMessage(messages.publishPearsonPlus)}>
      {showProgress === false ?
        <AugmentedModalContent>
          <ModalContent>
            <FormField>
              <Input
                label={intl.formatMessage(messages.productId)}
                placeholder={intl.formatMessage(messages.productIdPlaceholder)}
                onBlur={handleProductIdBlur}
                onChange={productIDChange}
                feedback={productIdFeedback}
                disabled={isProductIdDisabled}
                value={productId}
              />
            </FormField>
            {isTocOptionsFFEnabled &&
              <FormField>
                <TocLabel>{intl.formatMessage(messages.organizationalLevel)}</TocLabel>
                {tocOptions.map((toc) => (
                  <TocItem key={toc}>
                    <Checkbox
                      label={toc}
                      checked={disabledTocLevel ? disabledTocLevel === toc : tocLevel === toc}
                      rounded={true}
                      onChange={tocLevelChange}
                      value={toc}
                      disabled={disabledTocLevel ? true : false}
                    />
                  </TocItem>
                ))}
                {
                  tocLevelFeedback &&
                  <TocFeedback>
                    <SVGIcon icon='warningCircle' />
                    {intl.formatMessage(messages.tocFeedback)}
                  </TocFeedback>
                }
              </FormField>}
            <FormField>
              <Select
                label={intl.formatMessage(messages.publishType)}
                feedback={typeFeedback ? intl.formatMessage(messages.typeFeedback) : ''}
                defaultValue={type}
                onChange={typeChange}>
                <option hidden disabled value=""></option>
                {Object.keys(publishType).map((key) => (<option
                  key={publishType[key]}
                  value={intl.formatMessage(messages[publishType[key]])}
                >
                  {intl.formatMessage(messages[publishType[key]])}
                </option>))}
              </Select>
            </FormField>
            <FormField>
              <Note
                multiline
                label={intl.formatMessage(messages.note)}
                placeholder={intl.formatMessage(messages.publishComment)}
                onChange={noteChange}
                feedback={noteFeedback ? intl.formatMessage(messages.noteFeedback) : ''}
              />
            </FormField>

            <InfoCard>
              <InfoCardLabel>{intl.formatMessage(messages.publishCardInfo)}</InfoCardLabel>
              <InfoCardList>
                <li>{intl.formatMessage(messages.publishCardInfoUpdate)}</li>
                <li>{intl.formatMessage(messages.publishCardInfoRefresh)}</li>
              </InfoCardList>
            </InfoCard>
          </ModalContent> :
          <ModalActions scrim>
            <Button primary onClick={handleDismiss}>{intl.formatMessage(messages.cancelButton)}</Button>
            <Button primary disabled={disablePublishButton} onClick={handlePublish}>{intl.formatMessage(messages.publishAllCaps)}</Button>
          </ModalActions>
        </AugmentedModalContent>
        :
        <div>
          <AugmentedModalContent>
            <CircularProgress>
              <Circular key={percentage} percentage={percentage} radius={78} trackWidth={12} trackColor={theme['color.greyDark']} progressFillColor={theme['color.greenLight']} />
              <StatusInfo exportStatus={exportStatus} />
            </CircularProgress>
          </AugmentedModalContent>
        </div>
      }
    </Modal >
  );
};

ExportPearsonPlus.propTypes = {
  onDismiss: PropTypes.func,
  visible: PropTypes.bool,
  onPublish: PropTypes.func,
  configSettings: PropTypes.object,
  status: PropTypes.string,
  exportSnippetsCompleted: PropTypes.number,
  totalExportSnippet: PropTypes.number,
  productIdPDZ: PropTypes.func,
  market: PropTypes.string,
};

const flagNames = [featureKeys.WORKSPACE_PUBLICATION_THREE_LEVEL_TOC_OPTION_BV];
export default compose(withTheme, enhanceWithFeatureFlagSubscription)(ExportPearsonPlus, flagNames);
